import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ESTIMATE_CUSTOM_UNITS,
  ESTIMATE_CUSTOM_VALUE,
  ESTIMATE_MINUTES_OPTIONS,
  customEstimateToMinutes,
  formatEstimateLabel,
  formatEstimateTime,
  isPresetEstimateMinutes,
  minutesToCustomEstimate,
} from '../../utils/estimateTime';
import { WORKFLOW_CLOSED_STATUS_ID, getActiveWorkflowByCategory, mergeReactionsWithWorkflowCategory } from '../../utils/reactionConfig';
import {
  consumeWorkTimerMinutes,
  ensureWorkTimers,
  peekWorkTimerMinutes,
} from '../../services/workTimerStore';

export const messageIsWorkflowClosed = (message) => {
  const reactions = mergeReactionsWithWorkflowCategory(
    message?.reactions,
    message?.workflowByCategory,
  );
  const byCategory = getActiveWorkflowByCategory(reactions);
  return Object.values(byCategory || {}).includes(WORKFLOW_CLOSED_STATUS_ID);
};

const consumeTimerTargets = async (targets) => {
  for (const target of targets || []) {
    const targetType = target?.targetType;
    const targetId = target?.targetId;
    if (!targetType || !targetId) continue;
    try {
      await consumeWorkTimerMinutes(targetType, targetId);
    } catch {
      // Timer may already be gone; keep the chosen minutes.
    }
  }
};

const collectSuggestedMinutes = async (options = {}) => {
  let suggested = Number(options.initialMinutes);
  if (!Number.isFinite(suggested) || suggested <= 0) suggested = null;
  let fromTimer = false;
  if (!options.targetType || !options.targetId) {
    return { suggested, fromTimer };
  }
  try {
    await ensureWorkTimers();
  } catch {
    // Use whatever is already cached.
  }
  const consumeTargets = [{ targetType: options.targetType, targetId: options.targetId }];
  const extras = Array.isArray(options.alsoConsume) ? options.alsoConsume : [];
  extras.forEach((extra) => {
    const extraType = extra?.targetType || options.targetType;
    const extraId = extra?.targetId;
    if (extraType && extraId) consumeTargets.push({ targetType: extraType, targetId: extraId });
  });
  let timerSum = 0;
  consumeTargets.forEach((target) => {
    const minutes = peekWorkTimerMinutes(target.targetType, target.targetId);
    if (minutes) timerSum += minutes;
  });
  if (timerSum > 0) {
    suggested = timerSum;
    fromTimer = true;
  }
  return { suggested, fromTimer, consumeTargets };
};

export const useCompletionTimeDialog = () => {
  const [session, setSession] = useState(null);
  const resolverRef = useRef(null);
  const consumeTargetsRef = useRef([]);

  const promptCompletionTime = useCallback(async (options = {}) => {
    const kind = options.kind === 'task' || options.kind === 'subtask' ? options.kind : 'workflow';
    const { suggested, fromTimer, consumeTargets } = await collectSuggestedMinutes(options);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      consumeTargetsRef.current = consumeTargets || [];
      setSession({
        kind,
        suggestedMinutes: suggested,
        fromTimer,
        confirmLabel: options.confirmLabel || 'Save and complete',
      });
    });
  }, []);

  const finish = useCallback((minutes) => {
    resolverRef.current?.(minutes);
    resolverRef.current = null;
    consumeTargetsRef.current = [];
    setSession(null);
  }, []);

  const completionTimeDialog = (
    <CompletionTimeDialog
      open={Boolean(session)}
      kind={session?.kind || 'workflow'}
      suggestedMinutes={session?.suggestedMinutes}
      fromTimer={Boolean(session?.fromTimer)}
      confirmLabel={session?.confirmLabel || 'Save and complete'}
      onCancel={() => finish(null)}
      onConfirm={async (minutes) => {
        await consumeTimerTargets(consumeTargetsRef.current);
        finish(minutes);
      }}
    />
  );

  return { promptCompletionTime, completionTimeDialog };
};

const applySuggestedMinutes = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) {
    return { value: '', customValue: '', customUnit: 'minutes' };
  }
  if (isPresetEstimateMinutes(n)) {
    return { value: String(n), customValue: '', customUnit: 'minutes' };
  }
  const custom = minutesToCustomEstimate(n);
  return {
    value: ESTIMATE_CUSTOM_VALUE,
    customValue: custom.value,
    customUnit: custom.unit,
  };
};

const CompletionTimeDialog = ({
  open,
  kind = 'workflow',
  suggestedMinutes = null,
  fromTimer = false,
  confirmLabel = 'Save and complete',
  onCancel,
  onConfirm,
}) => {
  const [value, setValue] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [customUnit, setCustomUnit] = useState('minutes');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const next = applySuggestedMinutes(suggestedMinutes);
      setValue(next.value);
      setCustomValue(next.customValue);
      setCustomUnit(next.customUnit);
      setError('');
      setSaving(false);
    }
  }, [open, suggestedMinutes]);

  if (!open) return null;

  const noun = kind === 'task' ? 'task' : kind === 'subtask' ? 'subtask' : 'workflow';
  const timerLabel = formatEstimateTime(suggestedMinutes);

  const handleConfirm = async () => {
    let minutes = null;
    if (value === ESTIMATE_CUSTOM_VALUE) {
      minutes = customEstimateToMinutes(customValue, customUnit);
      if (minutes == null) {
        setError('Enter how long it took');
        return;
      }
    } else {
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setError('Select how long it took');
        return;
      }
      minutes = parsed;
    }
    setSaving(true);
    try {
      await onConfirm(minutes);
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[2200] overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              How long did this take?
            </h3>
            <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
              Tell us how much time it took to finish this {noun} so we can track it properly.
            </p>
            {fromTimer && timerLabel ? (
              <p className="mt-2 text-[12px] font-medium text-emerald-700 dark:text-emerald-300">
                Timer recorded {timerLabel}. Change it if that isn’t the time it took.
              </p>
            ) : null}
          </div>
          <div className="px-5 py-4 space-y-3">
            <label className="block">
              <span className="block text-[12px] font-semibold text-gray-600 dark:text-gray-300 mb-1.5">
                Time to finish
              </span>
              <select
                value={value}
                onChange={(event) => {
                  setValue(event.target.value);
                  setError('');
                }}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
              >
                <option value="">Select time</option>
                {ESTIMATE_MINUTES_OPTIONS.map((minutes) => (
                  <option key={minutes} value={String(minutes)}>
                    {formatEstimateLabel(minutes)}
                  </option>
                ))}
                <option value={ESTIMATE_CUSTOM_VALUE}>Custom</option>
              </select>
            </label>
            {value === ESTIMATE_CUSTOM_VALUE ? (
              <div className="grid grid-cols-2 gap-3">
                <label className="min-w-0">
                  <span className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1">Value</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={customValue}
                    onChange={(event) => {
                      setCustomValue(event.target.value);
                      setError('');
                    }}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                    placeholder="e.g. 2"
                  />
                </label>
                <label className="min-w-0">
                  <span className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1">Unit</span>
                  <select
                    value={customUnit}
                    onChange={(event) => setCustomUnit(event.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                  >
                    {ESTIMATE_CUSTOM_UNITS.map((unit) => (
                      <option key={unit.value} value={unit.value}>{unit.label}</option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}
            {error ? <p className="text-[12px] text-red-600 dark:text-red-400">{error}</p> : null}
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-3 py-1.5 text-[13px] font-semibold rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={saving}
              className="px-3 py-1.5 text-[13px] font-semibold rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
            >
              {saving ? 'Saving…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CompletionTimeDialog;
